import { useState, createContext, useContext, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL || "";

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef(null);
  const tokenRef = useRef(null); // keep a sync ref for refresh callback

  const scheduleRefresh = useCallback((jwt) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    try {
      const payload = JSON.parse(atob(jwt.split(".")[1]));
      const expiresAt = payload.exp * 1000;
      const refreshIn = Math.max(expiresAt - Date.now() - 3600000, 60000);
      refreshTimer.current = setTimeout(() => {
        doRefresh(tokenRef.current);
      }, refreshIn);
    } catch {}
  }, []); // no deps — uses tokenRef instead of closure over token

  const doRefresh = async (currentToken) => {
    if (!currentToken) return;
    try {
      const res = await fetch(`${API}/api/auth/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        _applyToken(data.token, data.user);
        localStorage.setItem("ph_auth", JSON.stringify({ token: data.token, user: data.user }));
        scheduleRefresh(data.token);
      }
    } catch {}
  };

  // Apply token+user synchronously to both state and axios header
  const _applyToken = (t, u) => {
    tokenRef.current = t;
    setToken(t);
    setUser(u);
    if (t) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  };

  // Auto-logout on 401 — install once
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 && tokenRef.current) {
          if (refreshTimer.current) clearTimeout(refreshTimer.current);
          _applyToken(null, null);
          localStorage.removeItem("ph_auth");
          sessionStorage.removeItem("ph_auth");
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Restore session on mount — runs once
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      try {
        const payload = JSON.parse(atob(urlToken.split(".")[1]));
        _applyToken(urlToken, payload);
        sessionStorage.setItem("ph_auth", JSON.stringify({ token: urlToken, user: payload }));
        const clean = window.location.pathname + (params.toString().replace(/[?&]?token=[^&]+/, "").replace(/^&/, "?") || "");
        window.history.replaceState({}, "", clean);
        setLoading(false);
        scheduleRefresh(urlToken);
        return;
      } catch {}
    }

    const saved = localStorage.getItem("ph_auth") || sessionStorage.getItem("ph_auth");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        _applyToken(parsed.token, parsed.user);
        scheduleRefresh(parsed.token);
      } catch {}
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = (tokenValue, userData, remember = false) => {
    _applyToken(tokenValue, userData);
    if (remember) {
      localStorage.setItem("ph_auth", JSON.stringify({ token: tokenValue, user: userData }));
    } else {
      sessionStorage.setItem("ph_auth", JSON.stringify({ token: tokenValue, user: userData }));
      localStorage.removeItem("ph_auth");
    }
    scheduleRefresh(tokenValue);
  };

  const logout = () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    _applyToken(null, null);
    localStorage.removeItem("ph_auth");
    sessionStorage.removeItem("ph_auth");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
